import styled from 'styled-components';
import {
  AiButton,
  Button,
  ButtonType,
  IconButton,
  LinkButton,
} from '@ardoq/button';
import { colors, radius, space } from '@ardoq/design-tokens';
import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { Icon, IconSize } from '@ardoq/icons';
import {
  ActionMenuItem,
  CommonActionMenuItemProps,
  isAiButton,
  isDropdownButton,
  isIconButton,
  isLinkButton,
} from './types';
import omit from 'lodash/omit';

const Divider = styled.hr`
  background-color: ${colors.borderSubtle};
  height: ${space.medium};
  width: 1px;
  border-style: none;
  margin: 0;
  border-radius: ${radius.small};
`;

const toCommonActionButtonProps = (action: CommonActionMenuItemProps) => {
  return {
    buttonType: action.buttonType,
    isDisabled: action.state === 'disabled',
    isLoading: action.state === 'loading',
    isActive: action.state === 'active',
  };
};

export const NewPageTopBarActionButton = ({
  action,
}: {
  action: ActionMenuItem;
}) => {
  if (action.type === 'divider') {
    return <Divider />;
  }

  if (isDropdownButton(action)) {
    return (
      <ButtonWithDropdown
        options={action.options}
        maxDropdownSize={action.maxDropdownSize}
        data-tooltip-text={action.isIconOnly ? action.label : undefined}
        hideChevron={action.isIconOnly ?? false}
        onMenuOpen={action.onMenuOpen}
        {...toCommonActionButtonProps(action)}
      >
        {action.icon && <Icon iconName={action.icon.name} />}
        {action.isIconOnly ? null : action.label}
      </ButtonWithDropdown>
    );
  }

  if (isAiButton(action)) {
    return (
      <AiButton
        onClick={action.onClick}
        aria-label={action.label}
        data-tooltip-text={action.label}
        {...omit(toCommonActionButtonProps(action), ['buttonType'])}
      />
    );
  }

  if (isIconButton(action)) {
    return (
      <IconButton
        onClick={action.onClick}
        iconName={action.icon.name}
        label={action.label}
        {...toCommonActionButtonProps(action)}
      />
    );
  }

  if (isLinkButton(action)) {
    return (
      <LinkButton
        href={action.href}
        onClick={action.onClick}
        buttonType={ButtonType.GHOST}
        data-tooltip-text={action.label}
      >
        <Icon
          iconName={action.icon?.name}
          label={action.label}
          color={colors.icon}
          iconSize={IconSize.MEDIUM}
        />
      </LinkButton>
    );
  }

  return (
    <Button
      onClick={action.onClick}
      data-tooltip-text={action.tooltipText}
      data-tour-id={action.dataTourId}
      {...toCommonActionButtonProps(action)}
    >
      {action.icon?.position !== 'right' && action.icon && (
        <Icon iconName={action.icon.name} />
      )}
      {action.label}
      {action.icon?.position === 'right' && (
        <Icon iconName={action.icon.name} />
      )}
    </Button>
  );
};
